import {WorkItemType} from '../../../shared/dtos/estimate-tree.dto';

/** A fact the comparable library reads that a work item has not recorded, and where staff answer it. */
export interface MissingFact {
  label: string;
  /** data-testid or data-review-field of the control that records it. */
  field: string;
  /** The operation whose controls hold it; the editor switches to it first. */
  operationType?: string;
}

/** What missingFacts reads: a saved tree, or the editor's unsaved edits shaped the same way. */
export interface ReviewableItem {
  workItemType: string;
  species?: string | null;
  height?: string | null;
  width?: string | null;
  trunkDiameter?: string | null;
  additionalDetails?: Record<string, unknown>;
  operations?: {operationType: string; details?: Record<string, unknown>}[];
  /** The item's photos (saved, or the editor's previews); only their number matters here. */
  images?: unknown[];
}

const SIZED: string[] = [WorkItemType.Tree, WorkItemType.TreeGroup, WorkItemType.FallenTree, WorkItemType.DebrisPile];
const TRUNKED: string[] = [WorkItemType.Tree, WorkItemType.TreeGroup, WorkItemType.FallenTree, WorkItemType.Stump];
const TREE_WORK = ['Tree Removal','Thinning', 'Custom Trim/Prune', 'Ground to Canopy Clearance', 'Crown Reduction'];

const blank = (value: unknown) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
// "0 Inches" is what the Android picker saves when nobody chose a size: no fact.
const blankOrZero = (value: unknown) => blank(value) || (typeof value === 'string' && /^0+(\.0+)?(\s*Inches)?$/i.test(value.trim()));

// What each operation needs answered, by the details key its control uses.
const OPERATION_FACTS: Record<string, [key: string, label: string]> = {
  'Stump Grinding': ['grindingDepthInches', 'grinding depth'],
  'Thinning': ['thinningAmount', 'thinning amount'],
  'Crown Reduction': ['reductionAreas', 'reduction areas'],
  'Ground to Canopy Clearance': ['clearanceAreas', 'clearance areas'],
  'Misc Task': ['descriptiveNote', 'task details'],
};

/** Facts the library reads that this item has not recorded, in the order the editor shows them. Never required. */
export function missingFacts(item: ReviewableItem): MissingFact[] {
  const missing: MissingFact[] = [];
  const type = item.workItemType;
  const details = item.additionalDetails ?? {};
  const operations = item.operations ?? [];
  const ops = operations.map(op => op.operationType);
  operations.forEach(op => {
    const needs = OPERATION_FACTS[op.operationType];
    if (needs && blank(op.details?.[needs[0]])) missing.push({label: needs[1], field: needs[0], operationType: op.operationType});
  });
  // A stump split off a tree by the October 2026 repair whose tree has photos but no AR trunk
  // shot carries stumpPhotoFromTree (SQ-630): a person picks the photo that shows the stump
  // from that tree's set. Attaching any photo answers it; the key stays as inert history.
  if (type === WorkItemType.Stump && !blank(details.stumpPhotoFromTree) && (item.images?.length ?? 0) === 0) {
    missing.push({label: 'stump photo', field: 'photos'});
  }
  if (type !== WorkItemType.Misc && !item.species) missing.push({label: 'species', field: 'fact-species'});
  if (SIZED.includes(type) && !item.height) missing.push({label: 'height', field: 'fact-height'});
  if (SIZED.includes(type) && !item.width) missing.push({label: 'width', field: 'fact-width'});
  // A stump's size is its own diameter, kept in the trunk column; a tree's trunk is a tree's.
  if (type === WorkItemType.Stump) {
    if (blankOrZero(item.trunkDiameter)) missing.push({label: 'stump diameter', field: 'fact-trunk'});
  } else if (TRUNKED.includes(type) && !item.trunkDiameter) {
    missing.push({label: 'trunk diameter', field: 'fact-trunk'});
  }
  if (type === WorkItemType.DebrisPile && !details.DebrisPile_length) missing.push({label: 'length', field: 'fact-length'});
  // A group's tree count, which a change to Tree Group leaves blank (SQ-671). The trunk count is not asked here: it
  // is unrecorded on almost every older tree (96% of completed ones in October 2026), so it would flag every item;
  // the type change's dialog names it when a change brings it in.
  if (type === WorkItemType.TreeGroup && blank(details.TreeGroup_treeCount)) missing.push({label: 'number of trees', field: 'fact-tree-count'});
  if (ops.includes('Tree Removal') && !details.removalStrategy) missing.push({label: 'removal plan', field: 'work-plan-removalStrategy'});
  if (ops.some(op => TREE_WORK.includes(op))) {
    if (!details.reachMethod) missing.push({label: 'reach method', field: 'work-plan-reachMethod'});
    if (!details.debrisMethod) missing.push({label: 'how debris comes down', field: 'work-plan-debrisMethod'});
    if (!details.riggingAmount) missing.push({label: 'rigging', field: 'work-plan-riggingAmount'});
  }
  return missing;
}
